// priceFormConfirmUtils.js
import {
  stationHasEUR,
  isFinitePositive,
  getLimitRange,
} from "./priceFormUtils";

/**
 * EUR je povoleno jen když:
 * - stanice má EUR
 * - fuel je v eurAllowedFuels (z backendu)
 */
export const isEurAllowedForStationFuel = (station, fuel, eurAllowedFuels) => {
  return stationHasEUR(station) && !!eurAllowedFuels?.has?.(fuel);
};

/**
 * Rekapitulace změn (dirty) pro jednu stanici.
 * EUR řádky, které nejsou povolené, se ukážou jako willSend=false.
 */
export const buildConfirmItemsForStation = ({ station, eurAllowedFuels }) => {
  const items = [];

  for (const [fuel, currencyData] of Object.entries(station?.prices || {})) {
    for (const [currency, price] of Object.entries(currencyData || {})) {
      if (!price?.dirty) continue;

      if (currency === "EUR" && !isEurAllowedForStationFuel(station, fuel, eurAllowedFuels)) {
        items.push({
          stationId: station.id,
          stationName: station.name,
          fuel,
          currency,
          originalValue: price?.originalValue ?? null,
          newValue: price?.value ?? null,
          source: price?.source ?? "manual",
          willSend: false,
          limitStatus: "n/a",
          reason: "EUR není povoleno",
        });
        continue;
      }

      const v = price?.value;
      const willSend = isFinitePositive(v);

      items.push({
        stationId: station.id,
        stationName: station.name,
        fuel,
        currency,
        originalValue: price?.originalValue ?? null,
        newValue: v ?? null,
        source: price?.source ?? "manual",
        willSend,
        limitStatus: "n/a",
        reason: willSend ? null : "Neplatná hodnota",
      });
    }
  }

  return items;
};

/**
 * Rekapitulace nepotvrzených hodnot (confirmed=0) pro "Potvrzuji že jsem změny provedl"
 */
export const buildUnconfirmedItemsForStation = ({ station }) => {
  const items = [];

  for (const [fuel, currencyData] of Object.entries(station?.prices || {})) {
    for (const [currency, price] of Object.entries(currencyData || {})) {
      if (Number(price?.confirmed) !== 0) continue;

      items.push({
        stationId: station.id,
        stationName: station.name,
        fuel,
        currency,
        originalValue: null,
        newValue: price?.value ?? null,
        source: "manual",
        willSend: true,
        limitStatus: "n/a",
        reason: null,
        kind: "confirm",
      });
    }
  }

  return items;
};

/**
 * Enrich: pro nearby změny označí "v limitu / mimo limit" dle callbacku.
 * Callback si řešíš v komponentě (protože potřebuje armexData).
 */
export const enrichLimits = (items, { armexData, stationData, isPriceWithinLimits }) => {
  return items.map((it) => {
    const st =
      it.stationId === armexData?.id
        ? armexData
        : (stationData || []).find((s) => s.id === it.stationId);

    if (!st) return { ...it, limitStatus: "n/a" };

    const isSpecial = ["Kurz", "TF zboží", "TF cig"].includes(it.fuel);
    const isArmex = (st?.is_armex === 1) || (it.stationId === armexData?.id);

    if (isArmex || isSpecial) return { ...it, limitStatus: "n/a" };
    if (!it.willSend) return { ...it, limitStatus: "n/a" };

    const ok = isPriceWithinLimits(st, it.fuel, it.currency, it.newValue);
    return { ...it, limitStatus: ok ? "v limitu" : "mimo limit" };
  });
};

/**
 * Pro NoChange modal: najdi nearby položky mimo limity (pro info).
 */
export const buildOutOfLimitNearbyItems = ({ stationData, armexData, eurAllowedFuels }) => {
  const items = [];

  for (const st of stationData || []) {
    if (st?.is_armex === 1) continue;

    for (const [fuel, currencyData] of Object.entries(st?.prices || {})) {
      if (["Kurz", "TF zboží", "TF cig"].includes(fuel)) continue;

      for (const [currency, price] of Object.entries(currencyData || {})) {
        if (currency === "EUR" && !isEurAllowedForStationFuel(st, fuel, eurAllowedFuels)) continue;

        const v = Number(price?.value);
        if (!Number.isFinite(v) || v <= 0) continue;

        const armexV = Number(armexData?.prices?.[fuel]?.[currency]?.value);
        if (!Number.isFinite(armexV) || armexV <= 0) continue;

        const diff = v - armexV;
        const { min, max } = getLimitRange(st.limits, fuel);
        const inLimit = diff >= min && diff <= max;

        if (inLimit) continue;

        items.push({
          stationId: st.id,
          stationName: st.name,
          fuel,
          currency,
          value: v,
          armexValue: armexV,
          diff,
          limitMin: min,
          limitMax: max,
          side: diff < min ? "pod" : "nad",
        });
      }
    }
  }

  items.sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));
  return items;
};
